import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import IconButton from '@atlaskit/button/icon/button';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Flex from '@atlaskit/primitives/components/flex';
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';
import Pressable from '@atlaskit/primitives/pressable';
import type { TriggerProps } from '@atlaskit/popup/types';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import FadeIn from '@atlaskit/motion/fade-in';
import { token } from '@atlaskit/tokens';

import { AtlassianIntelligenceLogoDark } from '@atlassian/ai-components/atlassian-intelligence-logo-dark';

import { fg } from '@confluence/feature-gating';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

const i18n = defineMessages({
	logoAltText: {
		id: 'ai-floating-context-menu.floating-context-menu-button.logo-alt-text',
		defaultMessage: 'Atlassian Intelligence',
		description: 'The alt text for the Atlassian Intelligence logo icon',
	},
});

const aiFloatingContextMenuStyles = xcss({
	borderRadius: 'radius.xlarge',
	padding: 'space.075',
	boxShadow: 'elevation.shadow.overlay',
	backgroundColor: 'elevation.surface.raised',
});

const aiFloatingContextMenuStylesInObjectSidebar = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: 'radius.full',
});

const dividerStyles = xcss({
	height: '100%',
	width: '1px',
	backgroundColor: 'color.background.neutral',
});

const dividerStylesInObjectSidebar = xcss({
	height: '24px',
	width: '1px',
	backgroundColor: 'color.background.neutral',
});

const defaultPressableStyles = xcss({
	display: 'inline-flex',
	background: token('color.background.neutral.subtle'),
	color: 'color.text.subtle',
	width: 'auto',
	maxWidth: '100%',
	boxSizing: 'border-box',
	position: 'relative',
	flexShrink: 0,
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	paddingBlock: 'space.025',
	paddingInlineEnd: 'space.150',
	paddingInlineStart: 'space.150',
	verticalAlign: 'middle',
	borderRadius: 'radius.small',
	transition: token('motion.button.hovered'),
	whiteSpace: 'nowrap',
	'::after': {
		borderRadius: 'inherit',
		inset: 'space.0',
		borderStyle: 'solid',
		borderWidth: 'border.width',
		pointerEvents: 'none',
		position: 'absolute',
	},
	':hover': {
		background: token('color.background.neutral.subtle.hovered'),
	},
});

const selectedPressableStyles = xcss({
	background: token('color.background.selected'),
	color: 'color.text.selected',
	transition: token('motion.button.hovered'),
	'::after': {
		content: '""',
		borderColor: 'color.border.selected',
		borderStyle: 'solid',
	},
	':visited': {
		color: 'color.text.selected',
	},
	':hover': {
		// @ts-expect-error
		color: token('color.text.selected'),
		background: token('color.background.selected.hovered'),
	},
	':active': {
		// @ts-expect-error
		color: token('color.text.selected'),
		background: token('color.background.selected.pressed'),
		transition: token('motion.button.pressed'),
	},
});

const pressableMotionStyles = xcss({
	transition: token('motion.button.hovered'),
	':hover': {
		transition: token('motion.button.hovered'),
	},
	':active': {
		transition: token('motion.button.pressed'),
	},
});

const pressableLegacyMotionStyles = xcss({
	transition: `background ${token('motion.duration.xshort')} ${token('motion.easing.out.practical')}`,
	':active': {
		transition: `background ${token('motion.duration.xshort')} ${token('motion.easing.out.practical')}`,
	},
});

const sideBarPressableStyles = xcss({
	borderRadius: 'radius.full',
	paddingBlock: 'space.075',
});

type AIFloatingContextMenuButtonProps = {
	dynamicActionText?: string;
	isDynamicActionSelected?: boolean;
	onDynamicActionClick?: () => void;
	onMenuButtonClick?: () => void;
	triggerProps?: TriggerProps;
	isMenuOpen?: boolean;
	isInObjectSideBar?: boolean;
};

export const AIFloatingSplitButton: FC<AIFloatingContextMenuButtonProps> = ({
	dynamicActionText,
	isDynamicActionSelected = false,
	onDynamicActionClick,
	onMenuButtonClick,
	triggerProps,
	isMenuOpen,
	isInObjectSideBar = false,
}) => {
	const { formatMessage } = useIntl();
	const renderServerPlaceholder = useIsSSROrHydrating();

	return (
		<Flex {...triggerProps}>
			<Inline
				xcss={[
					!isInObjectSideBar && aiFloatingContextMenuStyles,
					isInObjectSideBar && aiFloatingContextMenuStylesInObjectSidebar,
				]}
				grow="hug"
				space="space.050"
				alignBlock="center"
			>
				<ExitingPersistence appear={!renderServerPlaceholder} exitThenEnter>
					{dynamicActionText && (
						<>
							<FadeIn entranceDirection="right" exitDirection="right" duration="medium">
								{(props) => (
									<div {...props} data-vc="ai-floating-dynamic-action">
										<Pressable
											onClick={() => onDynamicActionClick?.()}
											xcss={[
												defaultPressableStyles,
												isDynamicActionSelected && selectedPressableStyles,
												isInObjectSideBar && sideBarPressableStyles,
												fg('platform-dst-motion-uplift-custom-button') && pressableMotionStyles,
												!fg('platform-dst-motion-uplift-custom-button') &&
													pressableLegacyMotionStyles,
											]}
										>
											{dynamicActionText}
										</Pressable>
									</div>
								)}
							</FadeIn>
							<Box
								xcss={[
									!isInObjectSideBar && dividerStyles,
									isInObjectSideBar && dividerStylesInObjectSidebar,
								]}
							/>
						</>
					)}
				</ExitingPersistence>
				<IconButton
					icon={AtlassianIntelligenceLogoDark}
					label={formatMessage(i18n.logoAltText)}
					onClick={() => onMenuButtonClick?.()}
					appearance="subtle"
					isSelected={isMenuOpen}
					shape={isInObjectSideBar ? 'circle' : 'default'}
				/>
			</Inline>
		</Flex>
	);
};
